import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import useInView from '../hooks/useInView';

const agents = [
  {
    id: 'hireon', tag: 'Agentic AI', name: 'HireOn AI', subtitle: 'Hiring agent',
    href: '/products/hireon', color: '#a78bfa',
    image: '/Hireon-mockup.png',
    imageObjectFit: 'contain',
    panelBg: '#e8e8f0',
    features: ['Automated Resume Screening', 'Precision Candidate Matching', 'Seamless Interview Scheduling', 'Smarter, Faster Hiring'],
  },
  {
    id: 'nexlead', tag: 'Agentic AI', name: 'NexLead AI', subtitle: 'Lead generation',
    href: '/products/nextlead', color: '#3d7eff',
    image: '/Nextlead.png',
    imageObjectFit: 'cover',
    panelBg: 'rgba(10,14,26,0.95)',
    features: ['Find the Right Leads. Instantly.', 'Score. Prioritize. Convert', 'One Unified Lead Generation System', 'Sales Performance, Supercharged'],
  },
  {
    id: 'digimark', tag: 'Agentic AI', name: 'DigiMark', subtitle: 'Content generation',
    href: '/products/digimark', color: '#34d399',
    image: '/Digimark.png',
    imageObjectFit: 'contain',
    panelBg: '#f5ede8',
    features: ['AI Content. Created in Seconds', 'Schedule Smarter. Post Faster', 'Insights That Drive Decisions', 'One Dashboard. Every Platform'],
  },
  {
    id: 'meeting-manager', tag: 'Agentic AI', name: 'Meeting Manager', subtitle: 'Automated meetings',
    href: '/products/meeting-manager', color: '#fb923c',
    image: '/Meetingmanager.png',
    imageObjectFit: 'cover',
    panelBg: 'rgba(10,14,26,0.95)',
    features: ['AI-Generated Meeting Summaries', 'Key Decisions. Tracked Automatically', 'Centralized, Searchable Meeting Records', 'Turn Conversations into Action'],
  },
  {
    id: 'invoice-agent', tag: 'Agentic AI', name: 'Invoice Agent', subtitle: 'Intelligent Invoice Processing',
    href: '/products/invoice-processing-agent', color: '#60b4ff',
    image: '/Invoiceagent.png',
    imageObjectFit: 'cover',
    panelBg: 'rgba(10,14,26,0.95)',
    features: ['Zero Manual Data Entry', 'Real-Time Validation. Every Invoice', 'Seamless ERP Reconciliation', 'Invoice Processing, Reimagined'],
  },
];

const AgenticSection = () => {
  const [current,   setCurrent]   = useState(0);
  const [animating, setAnimating] = useState(false);
  const [ref, inView] = useInView();

  const goTo = useCallback((idx) => {
    if (animating) return;
    setAnimating(true);
    setCurrent((idx + agents.length) % agents.length);
    setTimeout(() => setAnimating(false), 400);
  }, [animating]);

  useEffect(() => {
    if (!inView) return;
    const t = setInterval(() => goTo(current + 1), 5000);
    return () => clearInterval(t);
  }, [current, inView, goTo]);

  const agent = agents[current];

  return (
    <section className="relative py-24 lg:py-32 bg-primary overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-blue-accent/4 rounded-full blur-[120px] pointer-events-none" />

      <div ref={ref} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Header ── */}
        <div
          className="text-center mb-12 lg:mb-16"
          style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.7s ease' }}
        >
          <div className="inline-flex items-center px-4 py-1.5 rounded-full border border-blue-accent/30 bg-white/5 text-xs text-blue-light font-body mb-5">
            Our Agentic AI
          </div>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-white mb-4">
            The Era of Agentic Intelligence
          </h2>
          <p className="text-muted font-body text-base max-w-xl mx-auto leading-relaxed">
            Meet your new digital workforce - AI agents that autonomously drive lead generation, hiring, and content creation at scale, with measurable impact and zero added overhead.
          </p>
        </div>

        {/* ══════════════════════════════════════════
            MOBILE / TABLET  (<lg)
            Card: image on top, content below
            Arrows: centred row below card
        ══════════════════════════════════════════ */}
        <div
          className="lg:hidden"
          style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(40px)', transition: 'all 0.8s ease 0.3s' }}
        >
          {/* Card */}
          <div
            className="rounded-2xl border overflow-hidden mx-auto w-full max-w-sm sm:max-w-md"
            style={{
              background:  'rgba(18,22,36,0.9)',
              borderColor: `${agent.color}35`,
              opacity:     animating ? 0 : 1,
              transform:   animating ? 'scale(0.96)' : 'scale(1)',
              transition:  'opacity 0.3s ease, transform 0.3s ease, border-color 0.4s ease',
            }}
          >
            {/* Image — top, fixed height, rounded top corners inherited from card */}
            <div
              className="relative overflow-hidden w-full"
              style={{
                background: agent.panelBg,
                height:     '220px',
              }}
            >
              <div
                className="absolute inset-0 pointer-events-none z-10"
                style={{ background: `radial-gradient(ellipse at 50% 40%, ${agent.color}18 0%, transparent 65%)` }}
              />
              <img
                src={agent.image}
                alt={agent.name}
                draggable={false}
                style={{
                  width:          '100%',
                  height:         '100%',
                  objectFit:      agent.imageObjectFit,
                  objectPosition: 'center',
                  display:        'block',
                }}
              />
            </div>

            {/* Content — below image */}
            <div className="px-5 py-6">
              {/* Badge row */}
              <div className="flex items-center gap-2.5 mb-4">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: `${agent.color}20`, color: agent.color }}
                >
                  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="8" cy="8" r="3"/>
                    <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.5 3.5l1.4 1.4M11.1 11.1l1.4 1.4M3.5 12.5l1.4-1.4M11.1 4.9l1.4-1.4"/>
                  </svg>
                </div>
                <span
                  className="px-3 py-1 rounded-full text-xs font-body border"
                  style={{ borderColor: `${agent.color}35`, color: agent.color, background: `${agent.color}12` }}
                >
                  {agent.tag}
                </span>
              </div>

              {/* Title + subtitle */}
              <h3 className="font-display font-extrabold text-2xl text-white mb-1">
                {agent.name}
              </h3>
              <p className="text-muted font-body text-sm mb-5">{agent.subtitle}</p>

              {/* Features */}
              <ul className="space-y-2.5 mb-6">
                {agent.features.map((f) => (
                  <li key={f} className="flex items-center gap-2.5 text-sm font-body text-white/80">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                      strokeWidth="2" strokeLinecap="round" stroke={agent.color} className="shrink-0">
                      <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    {f}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link
                to={agent.href}
                className="w-full inline-flex items-center justify-center px-6 py-3 rounded-xl text-sm font-display font-semibold text-always-white transition-all duration-300 hover:scale-105 active:scale-95"
                style={{
                  background: `linear-gradient(135deg, ${agent.color}, ${agent.color}bb)`,
                  boxShadow:  `0 4px 20px ${agent.color}30`,
                }}
              >
                Learn More
              </Link>
            </div>
          </div>

          {/* Mobile dots + arrows — centred row below card */}
          <div className="flex items-center justify-center gap-5 mt-6">
            {/* Prev */}
            <button
              onClick={() => goTo(current - 1)}
              className="w-10 h-10 rounded-full border border-border bg-card hover:bg-card-hover hover:border-blue-accent/40 flex items-center justify-center text-muted hover:text-white transition-all duration-200"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>

            {/* Dots */}
            <div className="flex gap-1.5">
              {agents.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className="rounded-full transition-all duration-300"
                  style={{ width: i === current ? 22 : 7, height: 7, background: i === current ? agent.color : 'rgba(255,255,255,0.18)' }}
                />
              ))}
            </div>

            {/* Next */}
            <button
              onClick={() => goTo(current + 1)}
              className="w-10 h-10 rounded-full bg-blue-accent hover:bg-blue-dark flex items-center justify-center text-white transition-all duration-200 hover:scale-105"
              style={{ boxShadow: '0 0 18px rgba(61,126,255,0.45)' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            DESKTOP  (lg+)
            Original side-by-side layout — UNCHANGED
        ══════════════════════════════════════════ */}
        <div
          className="hidden lg:block"
          style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(40px)', transition: 'all 0.8s ease 0.3s' }}
        >
          <div className="relative flex items-center gap-4">
            {/* Prev */}
            <button
              onClick={() => goTo(current - 1)}
              className="shrink-0 w-11 h-11 rounded-full border border-border bg-card hover:bg-card-hover hover:border-blue-accent/40 flex items-center justify-center text-muted hover:text-white transition-all duration-200 z-10"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>

            {/* Card */}
            <div
              className="light-card flex-1 rounded-2xl border overflow-hidden"
              style={{
                background:  'rgba(18,22,36,0.8)',
                borderColor: `${agent.color}25`,
                opacity:     animating ? 0 : 1,
                transform:   animating ? 'scale(0.97)' : 'scale(1)',
                transition:  'opacity 0.3s ease, transform 0.3s ease, border-color 0.4s ease',
                height:      '420px',
              }}
            >
              <div className="grid grid-cols-2 h-full">
                {/* Image panel */}
                <div
                  className="relative overflow-hidden"
                  style={{ background: agent.panelBg, height: '100%' }}
                >
                  <div
                    className="absolute inset-0 pointer-events-none z-10"
                    style={{ background: `radial-gradient(ellipse at 30% 50%, ${agent.color}12 0%, transparent 60%)` }}
                  />
                  <img
                    src={agent.image}
                    alt={agent.name}
                    draggable={false}
                    style={{
                      width:          '100%',
                      height:         '100%',
                      objectFit:      agent.imageObjectFit,
                      objectPosition: 'center',
                      display:        'block',
                      transition:     'transform 0.7s ease',
                    }}
                    className="hover:scale-[1.03]"
                  />
                </div>

                {/* Content panel */}
                <div
                  className="flex flex-col justify-center overflow-y-auto"
                  style={{ height: '100%', padding: '28px 32px' }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: `${agent.color}20`, color: agent.color }}
                    >
                      <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="8" cy="8" r="3"/>
                        <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.5 3.5l1.4 1.4M11.1 11.1l1.4 1.4M3.5 12.5l1.4-1.4M11.1 4.9l1.4-1.4"/>
                      </svg>
                    </div>
                    <span
                      className="px-3 py-1 rounded-full text-xs font-body border"
                      style={{ borderColor: `${agent.color}30`, color: agent.color, background: `${agent.color}10` }}
                    >
                      {agent.tag}
                    </span>
                  </div>

                  <h3 className="font-display font-extrabold text-2xl lg:text-3xl text-white mb-1">
                    {agent.name}
                  </h3>
                  <p className="text-muted font-body text-sm mb-5">{agent.subtitle}</p>

                  <ul className="space-y-2 mb-6">
                    {agent.features.map((f) => (
                      <li key={f} className="flex items-center gap-2.5 text-sm font-body text-white/80">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                          strokeWidth="2" strokeLinecap="round" stroke={agent.color} className="shrink-0">
                          <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                          <polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={agent.href}
                    className="inline-flex items-center justify-center px-6 py-3 rounded-xl text-sm font-display font-semibold text-always-white transition-all duration-300 hover:scale-105 active:scale-95"
                    style={{
                      background: `linear-gradient(135deg, ${agent.color}, ${agent.color}bb)`,
                      boxShadow:  `0 4px 20px ${agent.color}30`,
                    }}
                  >
                    Learn More
                  </Link>
                </div>
              </div>
            </div>

            {/* Next */}
            <button
              onClick={() => goTo(current + 1)}
              className="shrink-0 w-11 h-11 rounded-full bg-blue-accent hover:bg-blue-dark flex items-center justify-center text-white transition-all duration-200 hover:scale-105 shadow-[0_0_20px_rgba(61,126,255,0.4)] z-10"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </div>

          {/* Desktop dots */}
          <div className="flex justify-center gap-2 mt-8">
            {agents.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className="rounded-full transition-all duration-300"
                style={{ width: i === current ? 28 : 8, height: 8, background: i === current ? '#3d7eff' : 'rgba(255,255,255,0.15)' }}
              />
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default AgenticSection;